import {
  Button,
  Input,
  Listbox,
  ListboxItem,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@heroui/react';
import React, { useId, useMemo, useRef, useState } from 'react';
import { FiChevronDown, FiSearch } from 'react-icons/fi';

import type { SelectOption } from '../../../../../../../../components/FormComponents';
import {
  DEFAULT_SUB_CATALOG_VALUE,
  filterSubCatalogOptions,
  getSubCatalogSelectionSummary,
} from '../../../../../../utils/subCatalogSelection';

interface SearchableCatalogPickerProps {
  options: SelectOption[];
  value: string[];
  onSelectionChange: (values: string[]) => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  errorMessage?: string;
}

export const SearchableCatalogPicker: React.FC<
  SearchableCatalogPickerProps
> = ({
  options,
  value,
  onSelectionChange,
  isLoading = false,
  isDisabled = false,
  errorMessage,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [triggerWidth, setTriggerWidth] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listboxId = useId();
  const filteredOptions = useMemo(
    () => filterSubCatalogOptions(options, query),
    [options, query]
  );
  const selectionSummary = useMemo(
    () => getSubCatalogSelectionSummary(options, value),
    [options, value]
  );
  const selectedCustomCount = value.filter(
    (id) => id !== DEFAULT_SUB_CATALOG_VALUE
  ).length;
  const hasCustomCatalogs = options.some(
    ({ value: id }) => id !== DEFAULT_SUB_CATALOG_VALUE
  );
  const popupWidth = `min(calc(100vw - 2rem), max(24rem, ${triggerWidth}px))`;

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setTriggerWidth(triggerRef.current?.getBoundingClientRect().width ?? 0);
    }

    setIsOpen(open);

    if (!open) setQuery('');
  };

  const handleSelectionChange = (keys: 'all' | Set<React.Key>) => {
    if (keys === 'all') return;

    const visibleIds = new Set(filteredOptions.map(({ value: id }) => id));
    const hiddenSelectedIds = value.filter((id) => !visibleIds.has(id));

    onSelectionChange(
      Array.from(
        new Set([
          ...hiddenSelectedIds,
          ...Array.from(keys, (key) => String(key)),
        ])
      )
    );
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-foreground">
        Custom catalog
      </label>

      <Popover
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        placement="bottom-start"
        shouldBlockScroll={false}
        disableAnimation
      >
        <PopoverTrigger>
          <Button
            ref={triggerRef}
            aria-label="Custom catalog"
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-controls={listboxId}
            aria-invalid={Boolean(errorMessage)}
            variant="bordered"
            isDisabled={isDisabled}
            isLoading={isLoading}
            disableRipple
            endContent={
              <FiChevronDown
                aria-hidden="true"
                className={`shrink-0 transition-transform ${
                  isOpen ? 'rotate-180' : ''
                }`}
              />
            }
            className={`min-h-12 h-auto w-full justify-between bg-default-100 px-3 py-2 text-left shadow-sm transition-colors hover:border-default-400 ${
              errorMessage ? 'border-danger' : 'border-default-300'
            }`}
          >
            <span className="min-w-0 truncate text-sm font-normal text-foreground">
              {isLoading ? 'Loading custom catalogs…' : selectionSummary}
            </span>
          </Button>
        </PopoverTrigger>

        <PopoverContent className="p-0" style={{ width: popupWidth }}>
          <div className="w-full p-2">
            <Input
              autoFocus
              type="search"
              aria-label="Search custom catalogs"
              placeholder="Search by catalog name or ID"
              value={query}
              onValueChange={setQuery}
              startContent={
                <FiSearch
                  aria-hidden="true"
                  className="shrink-0 text-default-500"
                />
              }
              size="sm"
              variant="bordered"
              classNames={{
                inputWrapper: 'bg-default-50',
              }}
            />

            <div
              aria-live="polite"
              className="flex items-center justify-between px-1 pb-1 pt-2 text-xs text-default-500"
            >
              <span>
                {hasCustomCatalogs
                  ? `${options.length - 1} custom catalogs`
                  : 'No custom catalogs available'}
              </span>
              <span>
                {selectedCustomCount > 0
                  ? `${selectedCustomCount} selected`
                  : 'Default selected'}
              </span>
            </div>

            <Listbox
              id={listboxId}
              aria-label="Custom catalogs"
              selectionMode="multiple"
              selectedKeys={value}
              onSelectionChange={handleSelectionChange}
              items={filteredOptions}
              emptyContent={
                <div className="space-y-1">
                  <p>
                    {query.trim()
                      ? `No catalogs match “${query.trim()}”.`
                      : 'No custom catalogs available.'}
                  </p>
                  {query.trim() && (
                    <p className="text-default-400">
                      Search by catalog name or ID.
                    </p>
                  )}
                </div>
              }
              classNames={{
                base: 'mt-2 max-h-80 overflow-y-auto p-0',
                list: 'gap-0',
                emptyContent: 'py-8 text-sm text-default-500',
              }}
              itemClasses={{
                base: 'rounded-lg px-3 py-2 data-[selected=true]:bg-primary-50',
              }}
              disableAnimation
            >
              {(option) => (
                <ListboxItem
                  key={option.value}
                  textValue={`${option.label} ${
                    option.description ?? option.value
                  }`}
                >
                  <div className="min-w-0 py-0.5 text-left">
                    <div className="truncate text-sm font-medium text-foreground">
                      {option.label}
                    </div>
                    {option.description && (
                      <div
                        title={
                          option.value === DEFAULT_SUB_CATALOG_VALUE
                            ? undefined
                            : option.description
                        }
                        className={`mt-0.5 truncate text-xs text-default-500 ${
                          option.value === DEFAULT_SUB_CATALOG_VALUE
                            ? ''
                            : 'font-mono'
                        }`}
                      >
                        {option.description}
                      </div>
                    )}
                  </div>
                </ListboxItem>
              )}
            </Listbox>
          </div>
        </PopoverContent>
      </Popover>

      {errorMessage && (
        <p role="alert" className="text-xs text-danger">
          {errorMessage}
        </p>
      )}
    </div>
  );
};
